import { GoogleMap, Marker } from '@react-google-maps/api';
import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { ReactComponent as CloseIcon } from '../../assets/icons/close-circle.svg';
import { ReactComponent as CCS2Icon } from '../../assets/icons/device-management/ccs2.svg';
import { ReactComponent as NewTabIcon } from '../../assets/icons/tables/new-tab.svg';
import { brandIcons, brandImage } from '../../config/branding';
import { useGoogleMaps } from '../../contexts/google-map-context/GoogleMapContent';
import { useAppDispatch } from '../../redux/hooks';
import { setNotificationDetails } from '../../redux/slice/NotificationSlice';
import device from '../../utils/api/device';

// import FacilitySidePanel from './FacilitySidePanel';
import StationSidePanel from './StationSidePanel';

interface DeviceSidePanelProperties {
  fetchIdFrom: 'queryParams' | 'props';
  id?: string;
}

interface Connector {
  connectorId: number;
  isReservationEnabled: boolean;
  status: string;
  _id: string;
  isAvailable: boolean;
}

interface ChargingPointProperties {
  _id?: string;
  chargingStation?: string;
  chargingPointName?: string;
  chargingPointSn?: string;
  maximumOutputPower?: string;
  maximumOutputPowerUnit?: string;
  connectorType?: string;
  isAvailable?: boolean;
  isDeviceOffline?: boolean;
  connectors: Connector[];
  chargePointModel?: string;
  chargePointVendor?: string;
  chargingStationName?: string;
  latitude?: string;
  longitude?: string;
  address?: string;
  accountCustomId?: string;
  accountName?: string;
}

/**
 * check the lat and lng is valid or not
 * @param position
 * @returns
 */
const validatePosition = (position: { latitude: any; longitude: any }) => {
  const lat = parseFloat(position.latitude);
  const lng = parseFloat(position.longitude);
  return !Number.isNaN(lat) && !Number.isNaN(lng) ? { lat, lng } : null;
};

/**
 * Device side panel
 * @returns
 */
export default function DeviceSidePanel(properties: DeviceSidePanelProperties) {
  const { fetchIdFrom, id } = properties;

  const { isLoaded } = useGoogleMaps();
  const dispatch = useAppDispatch();
  const [searchParameters] = useSearchParams();

  const mapReference = useRef<google.maps.Map | null>(null);

  const [deviceDetails, setDeviceDetails] = useState<ChargingPointProperties>({
    connectors: [],
  });

  const [overlaySidePanel, setOverlaySidePanel] = useState<'closed' | 'station' | 'facility'>(
    'closed',
  );

  const [deviceImage, setDeviceImage] = useState<string>('');

  const deviceGeneralDetails = [
    {
      id: 1,
      label: 'Station Name',
      key: 'chargingStationName',
      /**
       * Open side panel for station view
       * @returns
       */
      handleClick: () => setOverlaySidePanel('station'),
    },
    {
      id: 2,
      label: 'Device Id',
      key: '_id',
    },
    {
      id: 1,
      label: 'Account Name',
      key: 'accountName',
      /**
       * Open side panel for station view
       * @returns
       */
      // handleClick: () => setOverlaySidePanel('facility'),
    },
    {
      id: 3,
      label: 'Device Model',
      key: 'chargePointModel',
    },
    {
      id: 4,
      label: 'Device Name',
      key: 'chargingPointName',
    },
    {
      id: 5,
      label: 'Device S.No',
      key: 'chargingPointSn',
    },
    {
      id: 6,
      label: 'Charging Point Vendor',
      key: 'chargePointVendor',
    },
    {
      id: 7,
      label: 'Firmware Version',
      key: 'firmwareVersion',
    },
  ];

  /**
   * Function to fetch station details
   * @param stationId
   */
  const handleFetchStationDetails = async (stationId: string) => {
    try {
      const response = await device.getChargingPointById(stationId);
      setDeviceDetails(response?.data);
      const imageResponse = await device.fetchDeviceImage({
        modelSeries: response?.data?.chargePointModel,
      });
      setDeviceImage(imageResponse?.data?.defaultMedias?.[0]?.url || '');
    } catch (error: any) {
      dispatch(
        setNotificationDetails({
          type: 'error',
          notificationDetails: {
            statusCode: error?.response?.data?.code || 200,
            message: error?.response?.data?.message || 'Something went wrong!',
          },
        }),
      );
    }
  };

  useEffect(() => {
    let stationId;
    if (fetchIdFrom === 'queryParams') {
      stationId = searchParameters.get('deviceId');
    } else if (fetchIdFrom === 'props') {
      stationId = id;
    }

    if (!stationId) {
      return;
    }

    handleFetchStationDetails(stationId);
  }, [searchParameters, id]);

  return (
    <div className="device-side-panel-wrap">
      <div className="device-side-panel-container">
        <div className="device-preview">
          <div className="device-image-status-wrap">
            <div className="device-status-text-wrap">
              <div
                className={`device-status-text-container ${deviceDetails.isDeviceOffline ? 'offline' : 'online'}`}>
                <div className="icon" />
                <p>{deviceDetails.isDeviceOffline ? 'Offline' : 'Online'}</p>
              </div>
            </div>
            {deviceImage.length ? (
              <img src={deviceImage} alt="" className="device-details-image" />
            ) : (
              <img src={brandImage.emptyChargerImage} alt="" className="device-details-image" />
            )}
            <div
              className={`device-status-container ${deviceDetails.isDeviceOffline ? 'offline' : 'online'}`}>
              <div className="static-container" />
              <div className="animation-container" />
            </div>
          </div>
          <div className="map-address-container">
            <div className="device-side-panel-map">
              <h6>Device Location</h6>
              <div className="map-container">
                {isLoaded ? (
                  <GoogleMap
                    mapContainerStyle={{
                      width: '100%',
                      height: '100%',
                    }}
                    center={{
                      lat: Number(deviceDetails.latitude),
                      lng: Number(deviceDetails.longitude),
                    }}
                    zoom={3}
                    onLoad={(map) => {
                      mapReference.current = map; // Assign the map instance to the reference
                    }}>
                    {deviceDetails &&
                      (() => {
                        const validPosition = validatePosition({
                          latitude: deviceDetails.latitude,
                          longitude: deviceDetails.longitude,
                        });
                        return validPosition ? (
                          <Marker
                            key="01"
                            position={validPosition}
                            // clusterer={clusterer}
                            icon={{
                              url: brandIcons.stationLocator,
                              scaledSize: new window.google.maps.Size(44, 44), // Width, Height
                            }}
                          />
                        ) : null;
                      })()}
                  </GoogleMap>
                ) : null}
              </div>
            </div>
            <div className="address-wrap">
              <h6>Address</h6>
              <p>{deviceDetails?.address}</p>
            </div>
          </div>
        </div>
        <div className="content-wrap-with-scroll">
          {deviceDetails?.connectors && deviceDetails.connectors.length ? (
            <div className="connector-wrap">
              {deviceDetails.connectors.map((connector: any, index: number) => (
                <div className="each-connector">
                  <div className="connector-type">
                    {/* To-do -> Need to configure icon based on connector type */}
                    <span>
                      <CCS2Icon />
                    </span>
                    <h6>{`(${deviceDetails.connectorType})`}</h6>
                  </div>
                  <p>Connector {connector.connectorId}</p>
                  <div className={`connector-status ${connector.status}`}>{connector.status}</div>
                  {deviceDetails?.connectors && index !== deviceDetails.connectors.length - 1 && (
                    <div className="right-border" />
                  )}
                </div>
              ))}
            </div>
          ) : (
            <h6>Connector data unavailable</h6>
          )}
        </div>
        <div className="device-side-panel-details">
          {deviceGeneralDetails.map((generalDetail) => (
            <div className="each-device-data">
              <h6>{generalDetail.label}</h6>
              <p
                role="presentation"
                onClick={generalDetail.handleClick ? generalDetail.handleClick : () => {}}
                className={generalDetail.handleClick ? 'hyperlink' : ''}>
                {String(deviceDetails[generalDetail.key as keyof ChargingPointProperties] || '-')}
              </p>
            </div>
          ))}
        </div>
      </div>
      <div className="device-side-panel-description" />
      <div
        className={`device-panel-station-view-wrap ${overlaySidePanel === 'station' || overlaySidePanel === 'facility' ? 'active' : ''}`}>
        <div className="side-panel-header">
          {/* Need to configure after new tab integration */}
          <span style={{ opacity: '0.5', cursor: 'not-allowed' }}>
            <NewTabIcon />
          </span>
          <span role="presentation" onClick={() => setOverlaySidePanel('closed')}>
            <CloseIcon />
          </span>
        </div>
        {overlaySidePanel === 'station' ? (
          <div className="side-panel-content">
            <StationSidePanel fetchIdFrom="props" id={deviceDetails.chargingStation} />
          </div>
        ) : (
          <div className="side-panel-content">
            {/* <FacilitySidePanel fetchIdFrom="props" id={deviceDetails.accountCustomId} /> */}
            <div />
          </div>
        )}
      </div>
    </div>
  );
}
